import {authorized, optionallyAuthorized} from "./ApiClient";
import {
    DocumentationDownloadViewModel,
    DocumentationImage,
    DocumentationPdf,
    DocumentedProductViewModel,
    PresignedUrl,
    ProductDocumentationViewModel
} from "./models";
import {uploadToPresignedUrl} from "./upload";

const documentation = (productId: string) => `/product/${encodeURIComponent(productId)}/documentation`;

// Product documentation: anyone can read it; its PDFs need a session; editing it and tracing PDFs is for site administrators.
export const DocumentationApi = {
    // The published products that have documentation, by name.
    list: () => optionallyAuthorized<DocumentedProductViewModel[]>('GET', '/documentation'),

    // 404 for an unpublished product, unless includeUnpublished (SiteAdmin).
    get: (productId: string, includeUnpublished?: boolean) => includeUnpublished
        ? authorized<ProductDocumentationViewModel>('GET', `${documentation(productId)}?includeUnpublished=true`)
        : optionallyAuthorized<ProductDocumentationViewModel>('GET', documentation(productId)),

    save: (productId: string, content: string) => authorized<ProductDocumentationViewModel>('PUT', documentation(productId), { content }),

    // The product's image library (SiteAdmin), newest first.
    listImages: (productId: string) => authorized<DocumentationImage[]>('GET', `${documentation(productId)}/image`),

    // Uploads an image into the library (SiteAdmin) as name (-2, -3… if that's taken), or as replaces' new picture
    // (it keeps its id, name and description). 400 if the file isn't really a PNG or JPEG within the limits.
    uploadImage: async (productId: string, file: File, onProgress?: (fraction: number) => void,
                        details: { name?: string, altText?: string, replaces?: string } = {}) => {
        const {uploadToken, upload} = await authorized<{ uploadToken: string, upload: PresignedUrl }>(
            'POST', `${documentation(productId)}/image`, { contentType: file.type, size: file.size });
        await uploadToPresignedUrl(upload, file, onProgress);
        return await authorized<DocumentationImage>('PUT', `${documentation(productId)}/image`, { uploadToken, ...details });
    },

    // A new name (unique within the product: duplicate otherwise) or description. The content needn't change.
    updateImage: (productId: string, imageId: string, changes: { name?: string, altText?: string }) =>
        authorized<DocumentationImage>('PUT', `${documentation(productId)}/image/${encodeURIComponent(imageId)}`, changes),

    // 409 image_in_use while the content shows it, unless force, which takes it out of the content too.
    deleteImage: (productId: string, imageId: string, force?: boolean) =>
        authorized<void>('DELETE', `${documentation(productId)}/image/${encodeURIComponent(imageId)}${force ? '?force=true' : ''}`),

    // Makes a PDF marked with the user's name and a new download ID. 429 after too many in an hour.
    createPdf: (productId: string) => authorized<DocumentationPdf>('POST', `${documentation(productId)}/pdf`),

    // The ID as printed on the PDF (DOC-XXXX-XXXX); case, dashes and spaces don't matter.
    findDownload: (downloadId: string) =>
        authorized<DocumentationDownloadViewModel>('GET', `/documentation-download/${encodeURIComponent(downloadId.trim())}`)
};
